import type { ComponentRef } from '@angular/core';
import {
  ApplicationRef,
  ChangeDetectorRef,
  DestroyRef,
  Directive,
  effect,
  ElementRef,
  HostListener,
  inject,
  input,
  NgZone,
  Renderer2,
  signal,
  untracked,
  ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { computePosition, flip, offset, Placement, shift } from '@floating-ui/dom';
import type { Observable, Subscription } from 'rxjs';
import { tap, timer } from 'rxjs';

import { TooltipComponent } from '../../components/tooltip/tooltip.component';
import { Maybe } from '../../types/base';
import { TooltipVisibility } from '../../types/tooltip-visibility.type';

@Directive({
  selector: '[evcTooltipHover]',
  standalone: true,
})
export class TooltipHoverDirective {
  readonly evcTooltipHover = input<string | Observable<string>>('');
  readonly evcTooltipOffset = input(4);
  readonly evcTooltipDelay = input(0);
  readonly evcTooltipPlacement = input<Placement>('right');
  readonly evcTooltipSize = input<'default' | 'compact'>('default');
  readonly evcTooltipVisible = input<TooltipVisibility>('auto');

  private componentRef: ComponentRef<TooltipComponent> | null = null; // Used in tests, so kept pseudo-private
  #scrollParent: HTMLElement | null = null;
  #scrollListener?: () => void;
  #showTooltipTimer?: Subscription;

  readonly #el = inject(ElementRef<HTMLElement>);
  readonly #changeDetectorRef = inject(ChangeDetectorRef);
  readonly #appRef = inject(ApplicationRef);
  readonly #viewContainerRef = inject(ViewContainerRef);
  readonly #renderer = inject(Renderer2);
  readonly #ngZone = inject(NgZone);
  readonly #destroyRef = inject(DestroyRef);

  // mix input + local values to control visibility
  readonly #localVisibility = signal<Maybe<TooltipVisibility>>(undefined);
  #syncVisibility = effect(() => {
    const visibility = this.evcTooltipVisible();
    untracked(() => this.setVisibility(visibility));
  });

  @HostListener('mouseenter')
  onMouseEnter(): void {
    this.#showTooltipTimer?.unsubscribe();
    this.#showTooltipTimer = timer(this.evcTooltipDelay())
      .pipe(
        tap(() => this.setVisibility(this.evcTooltipVisible())),
        tap(() => this.showTooltip()),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  @HostListener('mouseleave')
  onMouseLeave(): void {
    this.#showTooltipTimer?.unsubscribe();
    this.#showTooltipTimer = undefined;
    this.destroy();
  }

  private setVisibility(visibility?: TooltipVisibility): void {
    const localValue: TooltipVisibility = (() => {
      if (visibility && visibility !== 'auto') return visibility;

      if (this.#el.nativeElement.offsetWidth < this.#el.nativeElement.scrollWidth) {
        return 'visible';
      }

      const children = Array.from<HTMLElement>(this.#el.nativeElement.children);
      if (children.some((child) => child.offsetWidth < child.scrollWidth)) {
        return 'visible';
      }

      return 'hidden';
    })();

    this.#localVisibility.set(localValue);
  }

  private showTooltip(): void {
    if (this.#localVisibility() !== 'visible' || this.componentRef !== null) {
      return;
    }

    this.componentRef = this.#viewContainerRef.createComponent(TooltipComponent);
    if (this.evcTooltipSize() === 'compact') {
      this.#renderer.addClass(this.componentRef.location.nativeElement, 'tooltip--compact');
    }

    if (this.#scrollParent === null) {
      this.#scrollParent = this.getScrollParent(this.#el.nativeElement);
    }

    this.#ngZone.runOutsideAngular(() => {
      // Run outside Angular to avoid triggering change detection on every scroll event
      if (this.#scrollParent) {
        this.#scrollListener = this.#renderer.listen(this.#scrollParent, 'scroll', () => {
          this.#ngZone.run(() => this.setTooltipComponentProperties());
        });
      }
    });

    this.setTooltipComponentProperties();
  }

  private getScrollParent(element: HTMLElement): HTMLElement | null {
    let parent: HTMLElement | null = element.parentElement;

    while (parent !== null) {
      const style: CSSStyleDeclaration = window.getComputedStyle(parent);
      const overflowY: string = style.overflowY;

      if (parent.scrollHeight > parent.clientHeight && overflowY !== 'hidden') {
        return parent;
      }

      parent = parent.parentElement;
    }

    return window.document.documentElement;
  }

  private setTooltipComponentProperties(): void {
    if (!this.componentRef) {
      return;
    }

    this.componentRef.instance.tooltip = this.evcTooltipHover();
    this.#changeDetectorRef.detectChanges();

    let placement: Placement = this.evcTooltipPlacement();

    if (this.#scrollParent !== null) {
      const elementRect: DOMRect = this.#el.nativeElement.getBoundingClientRect();
      const parentRect: DOMRect = this.#scrollParent?.getBoundingClientRect();

      if (elementRect.top < parentRect.top) {
        placement = 'bottom';
      } else if (elementRect.bottom > parentRect.bottom) {
        placement = 'top';
      }
    }

    computePosition(this.#el.nativeElement, this.componentRef?.location.nativeElement, {
      placement,
      middleware: [flip(), shift(), offset(this.evcTooltipOffset())],
    }).then(({ x, y }) => {
      if (this.componentRef !== null) {
        this.componentRef.instance.left = x;
        this.componentRef.instance.top = y;
        this.componentRef.changeDetectorRef.detectChanges();
      }
    });
  }

  private destroy(): void {
    if (this.componentRef !== null) {
      this.#appRef.detachView(this.componentRef.hostView);
      this.componentRef.destroy();
      this.componentRef = null;
    }

    if (this.#scrollListener) {
      this.#scrollListener();
    }

    // Also cancel timer if destroy is called directly
    if (this.#showTooltipTimer) {
      this.#showTooltipTimer.unsubscribe();
      this.#showTooltipTimer = undefined;
    }
  }
}
